import { useState } from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { useMe, useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { requiresMapApiKeys } from '@/api/types/settings';
import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HIGHLIGHT_RING, useHighlightSection } from '@/hooks/use-highlight-section';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { SettingsCard, SettingsSection } from '@/pages/settings/components/SettingsSection';

import { ApiKeyField } from './ApiKeyField';
import { ClientCredentialsForm } from './ClientCredentialsForm';
import { useInPostConnect } from './useInPostConnect';

// TODO: point this at the real merchant-facing connection guide once one exists.
const CONNECTION_GUIDE_URL = '#todo';

/** InPost docs: how to generate a Geowidget production token. */
const GEOWIDGET_GUIDE_URL =
  'https://dokumentacja-inpost.atlassian.net/wiki/spaces/PL/pages/58327041/Geowidget+v5+ENG+PL+Market#Production-Environment';

/** Figma 347-7126: 12px medium, underlined, muted. */
const inlineLinkClasses = 'text-xs font-medium text-muted-foreground underline';

export function AuthorizationPage() {
  const { t } = useTranslation('authorization');
  usePageTitle(t('title'));
  const navigate = useNavigate();

  const inpost = useInPostConnect();
  const {
    status,
    isConnected,
    needsOrgSelection,
    isStatusLoading,
    isStatusError,
    isConnecting,
    isConnectingClientCredentials,
    isDisconnecting,
    isBusy,
    canUseOAuthLogin,
  } = inpost;

  // Connect-method tab (prototype-3 segmented control). Defaults to the browser
  // login when the market allows it, else to client credentials - the only
  // method available outside Poland (PKCE stays visible but greyed out).
  const [methodTab, setMethodTab] = useState<string | null>(null);
  const activeMethodTab = methodTab ?? (canUseOAuthLogin ? 'oauth' : 'client-credentials');

  const meQuery = useMe();
  const updateKeys = useUpdateMerchantSettings();

  const settings = meQuery.data?.settings;

  const saveApiKey =
    (key: 'geocoding_api_key' | 'google_maps_api_key' | 'inpost_geowidget_token') =>
    (value: string) => {
      const next = value.trim() === '' ? null : value.trim();
      const saved = settings?.[key] ?? null;
      if (next === saved) return;
      updateKeys.mutate(
        { settings: { [key]: next } },
        {
          onSuccess: () => toast.success(t('saved')),
          onError: (error) => toastProblem(toApiProblem(error)),
        },
      );
    };

  // The map-based point modes (configured on the Settings → Checkout tab) need
  // these keys; mark them required when such a mode is active.
  const keysRequired = requiresMapApiKeys(settings?.point_selection_mode);

  const loginHighlight = useHighlightSection<HTMLElement>('inpost-connection');
  const geowidgetHighlight = useHighlightSection<HTMLDivElement>('geowidget-token');
  // Google keys pair - target of the settings Point-selection "set up" link.
  const apiKeysHighlight = useHighlightSection<HTMLDivElement>('api-keys');

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Compact 24px title, matching /settings (prototype-3). */}
      <h2 className="text-2xl font-semibold tracking-[-0.6px] text-black">{t('title')}</h2>

      <div className="flex w-full flex-col gap-6">
        {/* Login - prototype-3 two-column section. */}
        <SettingsSection
          ref={loginHighlight.ref}
          title={t('login.title')}
          description={t('login.description')}
          className={cn(loginHighlight.isHighlighted && HIGHLIGHT_RING)}
        >
          <SettingsCard>
            <div className="py-5">
              {isStatusError ? (
                <p className="text-sm text-destructive">{t('login.statusError')}</p>
              ) : isConnected ? (
                <div className="flex flex-col gap-2.5">
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    {needsOrgSelection ? (
                      <Button
                        variant="outline"
                        className="rounded-[10px] border-black text-black shadow-none hover:bg-accent"
                        type="button"
                        disabled={isBusy}
                        onClick={() => navigate('/connect/inpost/select-organization')}
                      >
                        {t('login.chooseOrganization')}
                      </Button>
                    ) : (
                      /* Figma 347-7115: outlined black status chip with 20px check. */
                      <span className="inline-flex h-9 items-center justify-center gap-2 rounded-[10px] border border-black px-4 py-2 text-sm font-medium leading-5 text-black">
                        <Check className="size-5 shrink-0" aria-hidden />
                        {t('login.connected')}
                      </span>
                    )}
                    {status?.auth_method === 'client_credentials' ? (
                      /* Prototype-3: yellow API-keys method badge. */
                      <span className="inline-flex items-center rounded-md bg-[#fdf3c7] px-2 py-1 text-xs font-medium text-[#8a6d00]">
                        {t('login.clientCredentials.badge')}
                      </span>
                    ) : null}
                    <a
                      href={CONNECTION_GUIDE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className={inlineLinkClasses}
                    >
                      {t('login.readGuide')}
                    </a>
                    <button
                      type="button"
                      onClick={inpost.disconnect}
                      disabled={isBusy}
                      className={`${inlineLinkClasses} disabled:opacity-50`}
                    >
                      {isDisconnecting ? t('login.disconnecting') : t('login.disconnect')}
                    </button>
                  </div>
                  {!needsOrgSelection && status?.organization_name ? (
                    <p className="text-[13px] text-muted-foreground">
                      {status.organization_name}
                      {status.organization_id
                        ? ` · ${t('login.organizationId', { id: status.organization_id })}`
                        : null}
                    </p>
                  ) : null}
                </div>
              ) : (
                /* Prototype-3: segmented method switch - browser login vs API keys. */
                <Tabs value={activeMethodTab} onValueChange={setMethodTab}>
                  <TabsList>
                    {canUseOAuthLogin ? (
                      <TabsTrigger value="oauth">{t('login.oauth.tab')}</TabsTrigger>
                    ) : (
                      /* Disabled triggers swallow hover (pointer-events-none), so the
                         tooltip listens on a focusable wrapper span instead. */
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0}>
                              <TabsTrigger value="oauth" disabled>
                                {t('login.oauth.tab')}
                              </TabsTrigger>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t('login.oauth.unavailable')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <TabsTrigger value="client-credentials">
                      {t('login.clientCredentials.tab')}
                    </TabsTrigger>
                  </TabsList>
                  {/* No display classes on TabsContent - `flex` would override the
                      `hidden` attribute Radix puts on the inactive pane. */}
                  <TabsContent value="oauth" className="mt-3.5">
                    <div className="flex flex-col gap-3.5">
                      <p className="text-sm text-muted-foreground">
                        {t('login.oauth.description')}
                      </p>
                      <div className="flex items-center gap-6">
                        <Button
                          variant="outline"
                          className="rounded-[10px] border-black text-black shadow-none hover:bg-accent"
                          type="button"
                          disabled={isBusy || isStatusLoading}
                          onClick={inpost.connect}
                        >
                          {isStatusLoading
                            ? t('login.statusLoading')
                            : isConnecting
                              ? t('login.connecting')
                              : t('login.connect')}
                        </Button>
                        <a
                          href={CONNECTION_GUIDE_URL}
                          target="_blank"
                          rel="noreferrer"
                          className={inlineLinkClasses}
                        >
                          {t('login.readGuide')}
                        </a>
                      </div>
                    </div>
                  </TabsContent>
                  <TabsContent value="client-credentials" className="mt-3.5">
                    <div className="flex flex-col gap-3.5">
                      <p className="text-sm text-muted-foreground">
                        {t('login.clientCredentials.description')}
                      </p>
                      <ClientCredentialsForm
                        onConnect={inpost.connectWithClientCredentials}
                        isConnecting={isConnectingClientCredentials}
                        disabled={isBusy || isStatusLoading}
                      />
                      <a
                        href={CONNECTION_GUIDE_URL}
                        target="_blank"
                        rel="noreferrer"
                        className={`${inlineLinkClasses} self-start`}
                      >
                        {t('login.readGuide')}
                      </a>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </SettingsCard>
        </SettingsSection>

        {/* API keys - prototype-3: one card, one row per key. */}
        <SettingsSection title={t('keys.title')} description={t('keys.description')}>
          <SettingsCard>
            <div
              ref={apiKeysHighlight.ref}
              className={cn(
                'divide-y divide-[#efefec]',
                apiKeysHighlight.isHighlighted && `rounded-xl ${HIGHLIGHT_RING}`,
              )}
            >
              <div className="py-5">
                <ApiKeyField
                  label={t('keys.geocodingApiKey')}
                  description={t('keys.geocodingApiKeyDescription')}
                  initialValue={settings?.geocoding_api_key ?? ''}
                  onSave={saveApiKey('geocoding_api_key')}
                  required={keysRequired}
                  validate
                />
              </div>
              <div className="py-5">
                <ApiKeyField
                  label={t('keys.googleMapsApiKey')}
                  description={t('keys.googleMapsApiKeyDescription')}
                  initialValue={settings?.google_maps_api_key ?? ''}
                  onSave={saveApiKey('google_maps_api_key')}
                  required={keysRequired}
                />
              </div>
            </div>
            <div
              ref={geowidgetHighlight.ref}
              className={cn(
                'py-5',
                geowidgetHighlight.isHighlighted && `rounded-xl ${HIGHLIGHT_RING}`,
              )}
            >
              <ApiKeyField
                label={t('keys.geowidgetToken')}
                initialValue={settings?.inpost_geowidget_token ?? ''}
                onSave={saveApiKey('inpost_geowidget_token')}
                description={
                  <>
                    {t('keys.geowidgetTokenDescription')}{' '}
                    <a
                      href={GEOWIDGET_GUIDE_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium underline"
                    >
                      {t('keys.geowidgetTokenGuide')}
                    </a>
                  </>
                }
              />
            </div>
          </SettingsCard>
        </SettingsSection>
      </div>
    </div>
  );
}
